import { useSession, useSupabase, useTenant } from "@how/app-kit";
import { useQuery } from "@tanstack/react-query";
import type { Tables } from "@how/database";

export type TenantRecord = Pick<
  Tables<"tenants">,
  "id" | "slug" | "name" | "status" | "time_zone" | "locale"
>;

/** The resolved tenant's row (members and invitees may read their own tenant). */
export function useTenantRecord() {
  const supabase = useSupabase();
  const tenant = useTenant();
  const tenantId = tenant.status === "resolved" ? tenant.tenant.tenantId : null;
  return useQuery({
    queryKey: ["tenant-record", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<TenantRecord> => {
      const { data, error } = await supabase
        .from("tenants")
        .select("id, slug, name, status, time_zone, locale")
        .eq("id", tenantId ?? "")
        .single();
      if (error) throw error;
      return data;
    },
  });
}

/** The signed-in user's own profile row (peers are readable too, so filter by the caller's id). */
export function useOwnProfile() {
  const supabase = useSupabase();
  const session = useSession();
  const userId = session.status === "signed_in" ? session.session.user.id : null;
  return useQuery({
    queryKey: ["own-profile", userId],
    enabled: userId !== null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, display_name, locale, time_zone")
        .eq("id", userId ?? "")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}
